import React, { useState, useCallback, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import {
  FiRefreshCw, FiLock, FiPlay,
  FiCheckCircle, FiAlertCircle, FiCalendar,
  FiUsers, FiTrendingUp, FiActivity, FiInfo,
  FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyM:'#1A5276', navyL:'#EBF4FF',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE', purpleB:'#7C3AED',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  border:'#E5E7EB', borderL:'#F3F4F6',
  bg:'#F4F6F9', white:'#FFFFFF',
  t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const fmtAmt = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPct = (n) => {
  if (n == null || n === '' || n === '—') return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num.toFixed(2) + '%';
};

const monthLabel = (ym) => {
  if (!ym) return '';
  try {
    return new Date(ym + '-01').toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  } catch { return ym; }
};

// Backend uses separate year + month integers, not YYYY-MM string
const ymToParams = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return { year: y, month: m };
};

const currentMonthYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const prevMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nextMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/* ─── Shimmer ─── */
const Sh = ({ h = 20, w = '100%', r = 6 }) => (
  <div style={{ height: h, width: w, borderRadius: r,
    background: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize: '400px 100%', animation: 'incShimmer 1.4s infinite' }}/>
);

/* ─── Spinner ─── */
const Spin = ({ color = '#fff', size = 14 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%',
    border: `2px solid rgba(255,255,255,0.35)`, borderTopColor: color,
    animation: 'incSpin 0.8s linear infinite' }}/>
);

/* ─── KPI Card ─── */
const Kpi = ({ label, value, icon, iconBg, iconColor, accent, loading }) => (
  <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
    padding: '16px 18px', boxShadow: '0 2px 6px rgba(13,59,94,0.06)',
    position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
      background: accent, borderRadius: '12px 12px 0 0' }}/>
    <div style={{ display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor }}>
        {icon}
      </div>
    </div>
    {loading
      ? <Sh h={26} w="55%"/>
      : <div style={{ fontSize: 22, fontWeight: 800, color: C.t1,
          letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 5 }}>{value ?? '—'}</div>}
    <div style={{ fontSize: 12, fontWeight: 600, color: C.t3 }}>{label}</div>
  </div>
);

/* ══════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════ */
export default function Incentive() {
  const [month,         setMonth]         = useState(prevMonth(currentMonthYM()));
  const [calcData,      setCalcData]      = useState([]);   // calculated results
  const [lockedData,    setLockedData]    = useState([]);   // already locked records
  const [lockedMonths,  setLockedMonths]  = useState([]);   // which months are locked
  const [calculating,   setCalculating]   = useState(false);
  const [locking,       setLocking]       = useState(false);
  const [loadingLocked, setLoadingLocked] = useState(false);
  const [calcDone,      setCalcDone]      = useState(false);
  const [search,        setSearch]        = useState('');
  const [usersMap,      setUsersMap]      = useState({}); // user id -> {first_name, last_name, email, designation}

  /* ── Check if selected month is already locked ── */
  const isMonthLocked = lockedMonths.includes(month);

  /* ── Fetch users for name lookup ── */
  useEffect(() => {
    api.getUsers({ page_size: 500 }).then(res => {
      const arr = Array.isArray(res?.data?.results) ? res.data.results
        : Array.isArray(res?.data) ? res.data : [];
      const map = {};
      arr.forEach(u => { map[u.id] = u; });
      setUsersMap(map);
    }).catch(() => {});
  }, []);

  /* ── Fetch previously locked incentive records ── */
  const fetchLocked = useCallback(async (mon) => {
    setLoadingLocked(true);
    try {
      const params = ymToParams(mon);
      const res  = await api.getIncentives(params);
      // Handle both { data: [...] } and { results: [...] } formats
      const d    = res?.data;
      const rows = Array.isArray(d?.data) ? d.data
        : Array.isArray(d?.results) ? d.results
        : Array.isArray(d) ? d : [];
      setLockedData(rows);
      const lockedRows = rows.filter(r => r.is_locked);
      if (lockedRows.length > 0) {
        setLockedMonths(prev => [...new Set([...prev, mon])]);
        setCalcData(rows);
        setCalcDone(true);
      } else {
        setCalcData([]);
        setCalcDone(false);
      }
    } catch { /* silent — locked data optional */ }
    finally { setLoadingLocked(false); }
  }, []);

  useEffect(() => { fetchLocked(month); }, [month, fetchLocked]);

  /* ── Extract rows from API response (handles both old & new format) ── */
  const extractRows = (d) => {
    // New format: { message, count, errors, data: [...] }
    if (Array.isArray(d?.data))    return d.data;
    // Standard paginated: { results: [...] }
    if (Array.isArray(d?.results)) return d.results;
    // Plain array
    if (Array.isArray(d))          return d;
    // Single object
    if (d?.id !== undefined)       return [d];
    return [];
  };

  /* ── Calculate ── */
  const handleCalculate = async () => {
    setCalculating(true);
    setCalcDone(false);
    setCalcData([]);
    try {
      const res    = await api.calculateIncentive(ymToParams(month));
      const resData = res?.data;
      const rows   = extractRows(resData);
      // Show any per-user errors returned by the backend
      const errors = Array.isArray(resData?.errors) ? resData.errors : [];
      errors.forEach(e => toast.warn(typeof e === 'string' ? e : JSON.stringify(e)));
      if (rows.length === 0) {
        toast.info('No disbursed + PDD-cleared cases found for this month.');
      } else {
        setCalcData(rows);
        setCalcDone(true);
        // Use backend message if available, fallback to count
        const msg = resData?.message || `Calculated for ${resData?.count ?? rows.length} user${rows.length !== 1 ? 's' : ''}`;
        toast.success(msg);
      }
    } catch (err) {
      toast.error(err?.message || err?.detail || 'Calculation failed. Try again.');
    } finally { setCalculating(false); }
  };

  /* ── Lock ── */
  const handleLock = async () => {
    if (!window.confirm(
      `Lock incentives for ${monthLabel(month)}?\n\nOnce locked, this cannot be changed.`
    )) return;
    setLocking(true);
    try {
      // Backend expects year + month as separate integers
      await api.lockIncentive(ymToParams(month));
      setLockedMonths(prev => [...new Set([...prev, month])]);
      toast.success(`Incentives locked for ${monthLabel(month)}`);
      fetchLocked(month);
    } catch (err) {
      toast.error(err?.message || err?.detail || 'Lock failed. Try again.');
    } finally { setLocking(false); }
  };

  /* ── Summary ── */
  const summary = calcData.reduce((acc, r) => {
    acc.users     += 1;
    acc.disbursed += Number(r.total_disbursement || 0);
    acc.bsaPayout += Number(r.total_bsa_payout   || 0);
    acc.netCo     += Number(r.total_net_to_company || 0);
    acc.incentive += Number(r.incentive_amount    || 0);
    return acc;
  }, { users: 0, disbursed: 0, bsaPayout: 0, netCo: 0, incentive: 0 });

  /* ── Filter — use user_detail from response (fallback to usersMap) ── */
  const filtered = calcData.filter(r => {
    if (!search) return true;
    const q  = search.toLowerCase();
    const ud = r.user_detail || usersMap[r.user] || {};
    const name = [ud.first_name, ud.last_name].filter(Boolean).join(' ').toLowerCase();
    return name.includes(q) || (ud.email || '').toLowerCase().includes(q);
  });

  /* ── Avatar color ── */
  const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#EF4444'];
  const aColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

  return (
    <>
      <style>{`
        @keyframes incShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes incSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes incFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .inc-anim { animation: incFadeUp 0.22s ease both; }
        .inc-row:hover td { background:#F8FAFC !important; }
        .inc-row { cursor: default; }
      `}</style>

      <Layout pageTitle="Incentive Calculation">
        <div className="inc-anim" style={{ display:'flex', flexDirection:'column', gap:14,
          fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:28 }}>

          {/* ══ HERO ══ */}
          <div style={{ borderRadius:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:'20px 28px', display:'flex', justifyContent:'space-between',
              alignItems:'center', flexWrap:'wrap', gap:14 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.48)',
                  marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  Finance
                </div>
                <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800,
                  letterSpacing:'-0.03em', lineHeight:1.2 }}>
                  Incentive Calculation
                </h2>
                <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                  Calculate and lock monthly incentives for disbursed + PDD-cleared cases
                </div>
              </div>
              {/* Month navigator */}
              <div style={{ display:'flex', alignItems:'center', gap:8,
                background:'rgba(255,255,255,0.10)', borderRadius:10,
                border:'1px solid rgba(255,255,255,0.18)', padding:'6px 10px' }}>
                {/* Prev arrow */}
                <button onClick={() => setMonth(prevMonth(month))}
                  style={{ width:28, height:28, borderRadius:7, background:'rgba(255,255,255,0.12)',
                    border:'1px solid rgba(255,255,255,0.20)', color:'rgba(255,255,255,0.75)',
                    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FiChevronLeft size={13}/>
                </button>

                {/* Month dropdown + Year dropdown */}
                <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:160 }}>
                  <select
                    value={month.split('-')[1]}
                    onChange={e => {
                      const newM = e.target.value;
                      const yr   = month.split('-')[0];
                      const ym   = `${yr}-${newM}`;
                      if (ym <= currentMonthYM()) setMonth(ym);
                    }}
                    style={{
                      background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.22)',
                      color:'#fff', borderRadius:7, height:28, padding:'0 4px 0 6px',
                      fontSize:13, fontWeight:700, cursor:'pointer', outline:'none',
                      fontFamily:'inherit', appearance:'none', WebkitAppearance:'none',
                      // Custom arrow via background
                      paddingRight:20,
                      backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='rgba(255,255,255,0.6)'/%3E%3C/svg%3E")`,
                      backgroundRepeat:'no-repeat', backgroundPosition:'right 6px center',
                    }}>
                    {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => {
                      const yr = month.split('-')[0];
                      const disabled = `${yr}-${m}` > currentMonthYM();
                      return (
                        <option key={m} value={m} disabled={disabled}
                          style={{ background:'#0D3B5E', color: disabled ? '#6B7280' : '#fff' }}>
                          {new Date(2000, i, 1).toLocaleString('en-IN', { month: 'short' })}
                        </option>
                      );
                    })}
                  </select>

                  <select
                    value={month.split('-')[0]}
                    onChange={e => {
                      const yr  = e.target.value;
                      const mon = month.split('-')[1];
                      const ym  = `${yr}-${mon}`;
                      const capped = ym > currentMonthYM() ? currentMonthYM() : ym;
                      setMonth(capped);
                    }}
                    style={{
                      background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.22)',
                      color:'#fff', borderRadius:7, height:28, padding:'0 4px 0 6px',
                      fontSize:13, fontWeight:700, cursor:'pointer', outline:'none',
                      fontFamily:'inherit', appearance:'none', WebkitAppearance:'none',
                      paddingRight:20,
                      backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='rgba(255,255,255,0.6)'/%3E%3C/svg%3E")`,
                      backgroundRepeat:'no-repeat', backgroundPosition:'right 6px center',
                    }}>
                    {(() => {
                      const curYear = new Date().getFullYear();
                      const years = [];
                      for (let y = curYear; y >= curYear - 4; y--) years.push(String(y));
                      return years.map(y => (
                        <option key={y} value={y} style={{ background:'#0D3B5E', color:'#fff' }}>{y}</option>
                      ));
                    })()}
                  </select>

                  {isMonthLocked && (
                    <div style={{ fontSize:10, fontWeight:700, color:'#4ADE80',
                      display:'flex', alignItems:'center', gap:3, whiteSpace:'nowrap' }}>
                      <FiLock size={10}/> LOCKED
                    </div>
                  )}
                </div>

                {/* Next arrow */}
                <button onClick={() => setMonth(nextMonth(month))}
                  disabled={month >= currentMonthYM()}
                  style={{ width:28, height:28, borderRadius:7,
                    background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.20)',
                    color: month >= currentMonthYM() ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.75)',
                    cursor: month >= currentMonthYM() ? 'not-allowed' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FiChevronRight size={13}/>
                </button>
              </div>
            </div>
          </div>

          {/* ══ FORMULA BANNER ══ */}
          

          {/* ══ LOCKED BANNER ══ */}
          {isMonthLocked && (
            <div style={{ background:C.greenL, border:`1px solid #86EFAC`, borderRadius:10,
              padding:'12px 18px', display:'flex', alignItems:'center', gap:10 }}>
              <FiLock size={16} style={{ color:C.greenB, flexShrink:0 }}/>
              <div>
                <div style={{ fontSize:13.5, fontWeight:700, color:C.green }}>
                  Incentives Locked — {monthLabel(month)}
                </div>
                <div style={{ fontSize:12.5, color:C.green, marginTop:2, opacity:0.8 }}>
                  These records are final and cannot be recalculated or modified.
                </div>
              </div>
            </div>
          )}

          {/* ══ ACTION BAR ══ */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            padding:'14px 20px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
            display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:12 }}>

            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <FiCalendar size={14} style={{ color:C.navy, opacity:0.7 }}/>
              <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>
                {monthLabel(month)}
              </span>
              {isMonthLocked && (
                <span style={{ background:C.greenL, color:C.greenB, padding:'2px 10px',
                  borderRadius:20, fontSize:11, fontWeight:700, border:`1px solid #86EFAC`,
                  display:'flex', alignItems:'center', gap:4 }}>
                  <FiLock size={10}/> Locked
                </span>
              )}
              {calcDone && !isMonthLocked && (
                <span style={{ background:C.skyL, color:C.skyB, padding:'2px 10px',
                  borderRadius:20, fontSize:11, fontWeight:700, border:`1px solid #BAE6FD` }}>
                  Calculated — not locked yet
                </span>
              )}
            </div>

            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              {/* Calculate button */}
              {!isMonthLocked && (
                <button onClick={handleCalculate} disabled={calculating || locking}
                  style={{ display:'flex', alignItems:'center', gap:7, height:38,
                    padding:'0 20px', borderRadius:9, border:'none',
                    background: calculating ? C.navyM : C.navy,
                    color:'#fff', fontWeight:700, fontSize:13, cursor:calculating?'not-allowed':'pointer',
                    fontFamily:'inherit', boxShadow:'0 2px 8px rgba(13,59,94,0.25)',
                    transition:'all 0.12s' }}
                  onMouseEnter={e=>{ if(!calculating&&!locking) e.currentTarget.style.background=C.navyD; }}
                  onMouseLeave={e=>{ if(!calculating) e.currentTarget.style.background=C.navy; }}>
                  {calculating
                    ? <><Spin color="#fff" size={13}/> Calculating…</>
                    : <><FiPlay size={13}/> Calculate</>}
                </button>
              )}

              {/* Lock button — only shown after calc, not yet locked */}
              {calcDone && !isMonthLocked && (
                <button onClick={handleLock} disabled={calculating || locking}
                  style={{ display:'flex', alignItems:'center', gap:7, height:38,
                    padding:'0 20px', borderRadius:9, border:'none',
                    background: locking ? '#15803D' : C.greenB,
                    color:'#fff', fontWeight:700, fontSize:13,
                    cursor: locking ? 'not-allowed' : 'pointer',
                    fontFamily:'inherit', boxShadow:'0 2px 8px rgba(22,163,74,0.30)',
                    transition:'all 0.12s' }}
                  onMouseEnter={e=>{ if(!locking) e.currentTarget.style.background='#15803D'; }}
                  onMouseLeave={e=>{ if(!locking) e.currentTarget.style.background=C.greenB; }}>
                  {locking
                    ? <><Spin color="#fff" size={13}/> Locking…</>
                    : <><FiLock size={13}/> Lock Month</>}
                </button>
              )}

              {/* Refresh locked data */}
              <button onClick={() => fetchLocked(month)} disabled={loadingLocked}
                title="Refresh"
                style={{ width:38, height:38, borderRadius:9, border:`1px solid ${C.border}`,
                  background:C.white, color:C.t3, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                <FiRefreshCw size={14}
                  style={{ animation: loadingLocked ? 'incSpin 1s linear infinite' : 'none' }}/>
              </button>
            </div>
          </div>

          {/* ══ KPI CARDS ══ */}
          {(calcDone || isMonthLocked) && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
              <Kpi label="Executives"       value={summary.users}
                icon={<FiUsers size={15}/>}      iconBg={C.navyL}   iconColor={C.navy}  accent={C.navy}   loading={loadingLocked}/>
              <Kpi label="Total Disbursed"  value={fmtAmt(summary.disbursed)}
                icon={<span style={{fontSize:15,fontWeight:700}}>₹</span>} iconBg={C.greenL}  iconColor={C.greenB} accent={C.greenB} loading={loadingLocked}/>
              <Kpi label="Net to Company"   value={fmtAmt(summary.netCo)}
                icon={<FiTrendingUp size={15}/>} iconBg={C.tealL}   iconColor={C.tealB} accent={C.tealB}  loading={loadingLocked}/>
              <Kpi label="Total Incentive"  value={fmtAmt(summary.incentive)}
                icon={<FiActivity size={15}/>}   iconBg={C.goldL}   iconColor={C.gold}  accent={C.goldB}  loading={loadingLocked}/>
            </div>
          )}

          {/* ══ TABLE ══ */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>

            {/* Table toolbar */}
            <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`,
              background:'#FAFBFC', display:'flex', alignItems:'center',
              justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <FiUsers size={14} style={{ color:C.navy, opacity:0.7 }}/>
                <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>
                  User-wise Incentive — {monthLabel(month)}
                </span>
                {calcData.length > 0 && (
                  <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px',
                    borderRadius:999, fontSize:10.5, fontWeight:700,
                    border:`1px solid rgba(13,59,94,0.12)` }}>{filtered.length}</span>
                )}
              </div>
              {calcData.length > 0 && (
                <div style={{ position:'relative', width:220 }}>
                  <span style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)',
                    fontSize:12, color:C.t4, pointerEvents:'none' }}>🔍</span>
                  <input type="text" placeholder="Search user…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width:'100%', height:32, border:`1px solid ${C.border}`, borderRadius:7,
                      padding:'0 10px 0 28px', fontSize:12.5, color:C.t1, background:C.bg,
                      outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
                    onFocus={e=>e.target.style.borderColor=C.navy}
                    onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              )}
            </div>

            {/* Loading shimmer */}
            {(calculating || loadingLocked) && (
              <div style={{ padding:18, display:'flex', flexDirection:'column', gap:10 }}>
                {[0,1,2,3].map(i => <Sh key={i} h={52}/>)}
              </div>
            )}

            {/* Empty — not yet calculated */}
            {!calculating && !loadingLocked && !calcDone && !isMonthLocked && (
              <div style={{ textAlign:'center', padding:'60px 24px' }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:C.navyL,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  margin:'0 auto 16px' }}>
                  <span style={{ fontSize:24, fontWeight:700, color:C.navy, opacity:0.5 }}>₹</span>
                </div>
                <div style={{ fontSize:15, fontWeight:700, color:C.t1, marginBottom:6 }}>
                  No data yet
                </div>
                <div style={{ fontSize:13, color:C.t3, marginBottom:18 }}>
                  Click <strong>Calculate</strong> to compute incentives for{' '}
                  <strong>{monthLabel(month)}</strong>
                </div>
                <button onClick={handleCalculate} disabled={calculating}
                  style={{ display:'inline-flex', alignItems:'center', gap:7,
                    padding:'10px 24px', borderRadius:9, border:'none',
                    background:C.navy, color:'#fff', fontWeight:700, fontSize:13,
                    cursor:'pointer', fontFamily:'inherit',
                    boxShadow:'0 2px 8px rgba(13,59,94,0.28)' }}>
                  <FiPlay size={13}/> Calculate {monthLabel(month)}
                </button>
              </div>
            )}

            {/* Empty — calculated but zero results */}
            {!calculating && !loadingLocked && calcDone && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'48px 24px' }}>
                <FiAlertCircle size={24} style={{ color:C.t4, marginBottom:10 }}/>
                <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>
                  {search ? `No users match "${search}"` : 'No eligible cases found'}
                </div>
                <div style={{ fontSize:13, color:C.t3 }}>
                  {search ? 'Try a different keyword'
                    : 'Incentives only apply to disbursed + PDD-cleared cases.'}
                </div>
              </div>
            )}

            {/* Table */}
            {!calculating && !loadingLocked && filtered.length > 0 && (
              <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {[
                        ['#',           '40px',  'center'],
                        ['Executive',   '200px', 'left'  ],
                        ['Disbursed',   '130px', 'right' ],
                        ['BSA Payout',  '120px', 'right' ],
                        ['Net to Co.',  '120px', 'right' ],
                        ['Excess',      '120px', 'right' ],
                        ['Incentive ₹', '130px', 'right' ],
                      ].map(([h, w, align]) => (
                        <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800,
                          color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign: align, borderBottom:`1px solid ${C.border}`,
                          whiteSpace:'nowrap', width: w }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => {
                      const isLast = idx === filtered.length - 1;
                      // user_detail object is now directly in response
                      // Fallback to usersMap for older API format
                      const ud    = r.user_detail || usersMap[r.user] || {};
                      const fn    = ud.first_name  || '';
                      const ln    = ud.last_name   || '';
                      const name  = [fn, ln].filter(Boolean).join(' ') || ud.email || `User #${r.user}`;
                      const email = ud.email        || '';
                      const desig = ud.designation  || '';

                       // Backend field names from the updated API response
                      const disbAmt = r.total_disbursement   || 0;
                      const bsaPay  = r.total_bsa_payout     || 0;
                      const netCo   = r.total_net_to_company || 0;
                      const excess  = r.excess_above_cutoff  || 0;
                      const incAmt  = r.incentive_amount     || 0;

                      const ac = aColor(name);
                      const hasIncentive = Number(incAmt) > 0;

                      return (
                        <tr key={r.id || r.user_id || idx} className="inc-row">
                          {/* # */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t4, textAlign:'center' }}>
                            {idx + 1}
                          </td>

                          {/* Executive */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <div style={{ width:32, height:32, borderRadius:'50%',
                                background:ac+'20', border:`1.5px solid ${ac}40`,
                                color:ac, display:'flex', alignItems:'center',
                                justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>
                                {name.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?'}
                              </div>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:C.t1 }}>{name}</div>
                                {(email || desig) && (
                                  <div style={{ fontSize:11.5, color:C.t4, marginTop:1 }}>
                                    {desig || email}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                           {/* Disbursed */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(disbAmt)}</span>
                          </td>

                          {/* BSA Payout */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13, color:C.t2 }}>{fmtAmt(bsaPay)}</span>
                          </td>

                          {/* Net to Company */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>{fmtAmt(netCo)}</span>
                          </td>

                          {/* Excess above cut-off */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13,
                              fontWeight: Number(excess) > 0 ? 700 : 400,
                              color: Number(excess) > 0 ? C.greenB : C.t4 }}>
                              {Number(excess) > 0 ? fmtAmt(excess) : '—'}
                            </span>
                          </td>

                          {/* Incentive ₹ — highlighted if > 0 */}
                          <td style={{ padding:'12px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            {hasIncentive ? (
                              <span style={{ fontSize:13, fontWeight:800, color:C.gold,
                                background:C.goldL, padding:'3px 10px', borderRadius:20,
                                border:'1px solid #FDE68A' }}>
                                {fmtAmt(incAmt)}
                              </span>
                            ) : (
                              <span style={{ fontSize:13, color:C.t4 }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Summary footer row */}
                  {filtered.length > 1 && (
                    <tfoot>
                      <tr style={{ background:'#F7F9FB', borderTop:`2px solid ${C.border}` }}>
                        <td colSpan={2} style={{ padding:'11px 14px', fontSize:12, fontWeight:800, color:C.t2 }}>
                          TOTAL ({filtered.length} users)
                        </td>
                        <td style={{ padding:'11px 14px', textAlign:'right', fontSize:13, fontWeight:800, color:C.greenB }}>
                          {fmtAmt(summary.disbursed)}
                        </td>
                        <td style={{ padding:'11px 14px', textAlign:'right', fontSize:13, fontWeight:800, color:C.tealB }}>
                          {fmtAmt(summary.bsaPayout)}
                        </td>
                        <td style={{ padding:'11px 14px', textAlign:'right', fontSize:13, fontWeight:800, color:C.navy }}>
                          {fmtAmt(summary.netCo)}
                        </td>
                        <td style={{ padding:'11px 14px' }}/>
                        <td style={{ padding:'11px 14px', textAlign:'right' }}>
                          <span style={{ fontSize:13, fontWeight:800, color:C.gold,
                            background:C.goldL, padding:'3px 10px', borderRadius:20,
                            border:'1px solid #FDE68A' }}>
                            {fmtAmt(summary.incentive)}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* Footer note */}
            {!calculating && !loadingLocked && calcData.length > 0 && (
              <div style={{ padding:'10px 18px', borderTop:`1px solid ${C.borderL}`,
                fontSize:11.5, color:C.t4, background:'#FAFBFC',
                display:'flex', alignItems:'center', gap:8 }}>
                {isMonthLocked
                  ? <><FiLock size={11}/> Locked — {monthLabel(month)}. Read-only.</>
                  : <><FiCheckCircle size={11}/> Calculated for {monthLabel(month)}. Click <strong style={{ color:C.greenB }}>Lock Month</strong> to finalize.</>}
              </div>
            )}
          </div>

          {/* ══ LOCK CONFIRMATION INFO ══ */}
          {calcDone && !isMonthLocked && (
            <div style={{ background:C.redL, border:`1px solid ${C.redBord}`, borderRadius:10,
              padding:'12px 18px', display:'flex', alignItems:'flex-start', gap:10 }}>
              <FiAlertCircle size={14} style={{ color:C.redB, marginTop:1, flexShrink:0 }}/>
              <span style={{ fontSize:12.5, color:'#7F1D1D', lineHeight:1.6 }}>
                <strong>Before locking:</strong> Verify all amounts carefully.
                Once locked, incentives for <strong>{monthLabel(month)}</strong> cannot be
                recalculated or changed. This action is irreversible.
              </span>
            </div>
          )}

        </div>
      </Layout>
    </>
  );
}
